import { stringify } from '@angular/compiler/src/util';
import { ComponentFactoryResolver, Injectable } from '@angular/core';
import { timeout } from 'rxjs/operators';
import { CHANNEL } from 'src/app/shared/constants/channel.constants';
import { ProductsBiocatchEnum, StepBiocatchEnum } from 'src/app/shared/enums/biocatchContext.enum';
import { OpEnum } from 'src/app/shared/enums/op.enum';
import { BUSINESS_CONST } from 'src/app/shared/constants/business.constants';

import { LogDeviceModel } from '../../../models/log-device.model';
import { AbreCuentasService } from '../../abre-cuentas/abre-cuentas.service';
import { ChannelCommunicatorService } from '../../communicator/channel-communicator/channel-communicator.service';
import {
  PlatformCommunicatorAuthService,
} from '../../communicator/platform-communicator/platform-communicator.auth.service';
import { ParametersService } from '../../parameters/parameters.service';
import { RequestService } from '../../request/request-onboarding.service';
import { CryptoVaultService } from '../crypto-vault/crypto-vault.service';
import { BioCatchConfigModel } from './../../../models/abre-cuentas.model';
import { FirmlyApprovedService } from '../../firmly-approved/firmly-approved.service';
import { ResponseBiocatchGeo } from 'src/app/shared/models/biocatch-context.model';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

declare function biocatchChangeContext(context: string): void;

@Injectable()

/**
 * Clase del servicio de BioCatch, capa de seguridad de la WebView de la aplicación
 *
 * *Biocatch service class, Security layer in the webview of the app*
 *
 * @implements {OnInit}
 */
export class BioCatchService {

  /**
   * Declaración de la variable currentCSID como string, identificador de la pantalla
   *
   * *Set currentCSID variable as string, Screen identifier*
   *
   * @access              public
   * @type                {string}
   */
  currentCSID: string;

  /**
   * Declaración de la variable currentScoreInfo como any, Registra la información de la puntuación
   *
   * *Set currentScoreInfo variable as any, registers Score info*
   *
   * @access              public
   * @type                {any}
   */
  currentScoreInfo: any;

  /**
   * Declaración de la variable devicePrint como LogDeviceModel, guarda la información del dispositivo
   *
   * *Set devicePrint variable as LogDeviceModel, saves device information*
   *
   * @access              public
   * @type                {LogDeviceModel}
   */
  devicePrint: LogDeviceModel;
  allyname: any;

  nitAlly: any;
  /**
   * Declaración de la variable document, contexto del navegador
   *
   * *Set document variable, navigator context*
   *
   * @access              public
   * @type                {string}
   */
  document = window.document;

  /**
   * Declaración de la variable cdApi como object, estrucutura del script
   *
   * *Set cdApi variable as object, script structure*
   *
   * @access              public
   * @type                {object}
   */
  cdApi = {
    resetSessionNumber: () => { return; },
    setCustomerSessionId: (_csid: string) => { return; },
    changeContext: (_context: string) => { return; },
    getCSID: () => { return; },
  };

  /**
   * Declaración de la variable isAppUnica para determirar la plataforma en la que esta la webview
   *
   * *Set isAppUnica variable as boolean, platform identifier*
   *
   * @access              private
   * @type                {boolean}
   */
  private isAppUnica: boolean;

  /**
   * Declaración de la variable isLogin para determirar postlogin
   *
   * *Set isLogin variable as boolean, postlogin identifier*
   *
   * @access              private
   * @type                {boolean}
   */
  private isLogin: boolean;

  /**
   * Bandera que valida si el servicio de Geolocalización funcionó
   * y respondió correctamente
   */
  private geoServiceComplete: boolean;

  /**
   * Declaración de la variable bodyFields para los campos de los llamados de update y get score
   *
   * *Set bodyFields for update and get score calls*
   *
   * @access              private
   * @type                {any}
   */
  private bodyFields: any;

  /**
   * Declaración de la variable userPPID parguardar el PPID del usuario
   *
   * *Set userPPID variable as string, user PPID*
   *
   * @access              private
   * @type                {string}
   */
  private userPPID: string;

  private ppidIsReady$ = new BehaviorSubject<boolean>(false);

  getUserPPID() {
    return this.userPPID;
  }

  /**
   * Constructor of the component.
   *
   * @param {AbreCuentasService}         abreCuentasService         Injectable abreCuentasService service.
   * @param {ChannelCommunicatorService} channelCommunicatorService Injectable channelCommunicatorService service.
   * @param {RequestService}             requestService             Injectable requestService service.
   */
  constructor(
    private abreCuentasService: AbreCuentasService,
    private channelCommunicatorService: ChannelCommunicatorService,
    private requestService: RequestService,
    private authService: PlatformCommunicatorAuthService,
    private parametersService: ParametersService,
    private firmlyApprovedService: FirmlyApprovedService,
    private cryptoVaultService: CryptoVaultService,
  ) { }

  /**
   * Función para inicializar la configuración para biocatch
   *
   * *Sets up biocatch config*
   *
   * @param {string} initialContext
   */

  async init(initialContext: string, chanelId: string, isLogged: boolean) {
    this.isLogin = isLogged;
    if (chanelId !== CHANNEL.PORTALALLIES.CODE) {
      this.isAppUnica = chanelId === CHANNEL.SUPERAPP.CODE ? true : false;
      const settingName = this.isAppUnica ? OpEnum.BIOCATCH_BODY_FIELDS_APP : OpEnum.BIOCATCH_BODY_FIELDS_PORTAL;
      this.bodyFields = this.parametersService.getSetting(settingName);
    } else {
      const settingName = OpEnum.BIOCATCH_BODY_FIELDS_ALLIES;
      this.bodyFields = this.parametersService.getSetting(settingName);
    }
    const bioCatchConfig = this.abreCuentasService.getBioCatchConfig();
    const sessionId = this.authService.getSessionId();
    if (this.isAppUnica && !this.isLogin) {
      await this.injectScriptsAppUnica(bioCatchConfig);
      await this.injectScriptPortal(bioCatchConfig, sessionId);
    } else if (this.isAppUnica && this.isLogin) {
      await this.injectScriptsAppUnica(bioCatchConfig);
    } else if (chanelId === CHANNEL.PORTALALLIES.CODE) {
      await this.injectScriptPortal(bioCatchConfig, sessionId);
      let allies;
      await this.requestService.documentsAllies()
        .then(res => allies = res.Ally)
        .catch(err => console.log(err));
      const ally = allies.filter(item => item.code_Ally === this.abreCuentasService.getAllyCode());
      this.allyname = ally[0].name_Ally;
      this.nitAlly = ally[0].nit_Ally;
    }

    // let product: ProductsBiocatchEnum;
    if (this.isLogin) {
      // await this.injectScriptPortal(bioCatchConfig, sessionId);
      this.currentCSID = this.abreCuentasService.getCSID();
      await this.callPpid();
    } else {
      this.setPpidIsReady(true);
    }
    const isOriginal = this.isAppUnica && !this.isLogin;
    setTimeout(() => {
      const product = this.abreCuentasService.getproductSelected();
      const product_code = product ? product.product_code as ProductsBiocatchEnum : undefined;
      this.changeContext(
        initialContext, this.firmlyApprovedService.isEmpty() && this.firmlyApprovedService.getOfferType_Channel() === 'En_firme' ?
        product_code : undefined, isOriginal, this.isEvidenteCase());
      /* (!this.firmlyApprovedService.isEmpty() && this.firmlyApprovedService.getOfferType_Channel() === 'En_firme')
        ? this.abreCuentasService.getproductSelected()?.product_code as ProductsBiocatchEnum
        : undefined, */
    }, 1000);
  }

  /**
   * Función para cargar los scripts para portal
   *
   * *Loads portal scripts function*
   *
   * @param {BioCatchConfigModel} bioCatchConfig
   * @param {string} sessionId
   */
  injectScriptPortal(bioCatchConfig: BioCatchConfigModel, sessionId: string) {
    this.loadScript(bioCatchConfig.scriptUrl)
      .then(() => {
        this.cdApi = this.document.defaultView ? this.document.defaultView['cdApi'] : {};
        this.resetSessionNumber();
        this.setSessionId(sessionId);
        return true;
      });
  }
  /**
   * Función para cargar los scripts para AppUnica
   *
   * *Loads appunica scripts function*
   *
   * @param {BioCatchConfigModel} bioCatchConfig
   */
  async injectScriptsAppUnica(bioCatchConfig: BioCatchConfigModel) {
    if (bioCatchConfig.bridgeUrl === undefined || bioCatchConfig.slaveUrl === undefined) {
      return;
    }
    await this.loadScript(bioCatchConfig.bridgeUrl)
      .then(() => {
        return true;
      });
    await this.loadScript(bioCatchConfig.slaveUrl)
      .then(() => {
        return true;
      });
  }

  /**
   * Función para cargar los scripts para biocatch
   *
   * *Loads biocatch scripts function*
   *
   * @param {string} url
   */
  loadScript(url: string): Promise<any> {
    const script = this.document.createElement('script');
    script.type = 'text/javascript';
    script.async = false;
    script.src = url;
    this.document.body.appendChild(script);
    return new Promise(resolve => {
      script.onload = () => resolve('loaded');
    });
  }

  /**
   * Función para reiniciar el número de sesión de Biocatch
   *
   * *Reset session number for biocatch*
   *
   */
  resetSessionNumber() {
    this.cdApi.resetSessionNumber();
  }

  /**
   * Función para inicializar el Id de la sesión para biocatch
   *
   * *Sets session id for biocatch*
   *
   *  @returns {string}
   */
  setSessionId(sessionId: string) {
    this.currentCSID = sessionId;
    this.cdApi.setCustomerSessionId(sessionId);
  }

  /**
   * Función para obtener la puntuación actual ip dirección
   *
   * *Gets current score Ip*
   *
   *  @returns {string}
   */
  getCurrentScoreIp(): string {
    if (!!this.currentScoreInfo && !!this.currentScoreInfo.ip) {
      return this.currentScoreInfo.ip;
    }
    return '';
  }

  /**
   * Función para obtener la configuración de Biocatch
   *
   * *Get biocatch info function*
   *
   * @returns {string}
   */
  getBiocatchInfo(): string {
    if (!!this.currentScoreInfo) {
      return this.currentScoreInfo;
    }
    return '';
  }

  /**
   * Devuelve el estado de consumo del servicio
   * de Geolocalización
   * True - Funcionó correctamente el servicio
   * False - Falló el servicio
   */
  getGeoServiceComplete(): boolean {
    return this.geoServiceComplete;
  }

  /**
   * Función para cambiar el contexto cambio de pantalla a nivel de biocatch
   *
   * *Change context function*
   *
   * @returns {Promise}
   */
  changeContext(context: StepBiocatchEnum | string, product?: ProductsBiocatchEnum, isOriginal = true, isPreloginOnly = false) {
    if ((this.isAppUnica && !this.isLogin && isOriginal)
      || (this.isAppUnica && !this.isLogin && isOriginal && product === ProductsBiocatchEnum.VIV)) {
      if (isOriginal && product && product === ProductsBiocatchEnum.VIV) {
        context = this.buildContext(context, product);
      } else {
        context = context.charAt(0).toUpperCase() + context.slice(1);
      }
      this.cdApi.changeContext(context);
    } else if ((this.isLogin && !isOriginal && !this.isEvidenteCase()) || (this.isEvidenteCase() && isPreloginOnly)) {
      const contextParam = this.buildContext(context, product);
      biocatchChangeContext(contextParam);
    }
  }

  /**
   * Arma el contexto correcto para biocatch apartir del paso y producto.
   *
   * *Form the correct context biocactch using step and product.*
   * @param {string|SteStepBiocatchEnum} step
   * @param {ProductsBiocatchEnum} product
   * @returns {string} Contexto biocatch armado segun la HU.
   *
   * * Biocatch context formed like to the definition. *
   */
  buildContext(step: string | StepBiocatchEnum, product?: ProductsBiocatchEnum): string {
    const isMulti = this.abreCuentasService.viewOptionMultiProduct;
    const contextParams = this.parametersService.getBiocatchContextParams();
    const channelId = this.channelCommunicatorService.getChannelId();
    let context = '';
    if (this.isLogin) {
      context = `${this.isAppUnica ? contextParams.baseContext.app : contextParams.baseContext.web}`;
      if (isMulti) {
        return `${context}_${step}_MUL`;
      }
    } else if (channelId === '163') {
      context = contextParams.baseContext.app_allies;
      const context2 = contextParams.baseContext.app_allies2;
      return `${context}${this.allyname}${context2}_${step}${product == undefined ? '' : '_' + product}`;
    } else if (isMulti) {
      context = `${this.isAppUnica ? contextParams.baseContext.app_pre : contextParams.baseContext.web_pre}`;
      return `${context}_${step}_MUL`;
    } else {
      context = `${this.isAppUnica ? contextParams.baseContext.app_pre : contextParams.baseContext.web_pre}`;
    }
    return `${context}_${step}${!!product ? '_' + contextParams.products[product] : ''}`;
  }

  /**
   * Función para revisar el origen del país
   *
   * *Check country function*
   *
   * @param {boolean} showLoading
   * @returns {Promise}
   */
  checkCountry(showLoading: boolean) {
    return this.getScore(showLoading).then(async result => {
      if (result['ipGeoCountry'] === undefined && result['ipGeoCity'] === undefined) {
        console.warn('Formateando Geolocalización por falla en el servicio...');
        result = this.validateGetScore(result);
        this.geoServiceComplete = false;
        this.saveDataInBitacora(StepBiocatchEnum.service_down);
      } else {
        this.geoServiceComplete = true;
        this.saveDataInBitacora(StepBiocatchEnum.service_up);
      }
      /* const contextData = JSON.stringify(result);
      this.saveDataInBitacora(`Data service Geo: ${contextData}`); */
      this.currentScoreInfo = result;
      result.rule = 'country';
      result.ip_geo_country = result.ipGeoCountry;
      const geoLocation = `${result.ipGeoLatitude},${result.ipGeoLongtitude}`;
      this.abreCuentasService.setGeoLocation(geoLocation);
      return this.checkParameterResults({ data: result }, showLoading);
    });
  }

  /**
   * Función que permite ajustar el JSON recibido del servicio de Geolocalización,
   * que por fallas en el servicio de Geo no llega completo, permitiendo así
   * completar todos los parámetros que se esperan para poder continuar el flujo
   * sin inconvenientes.
   * @param {any} dataGetScore - JSON incompleto recibido del servicio de Geo.
   * @returns {ResponseBiocatchGeo} - JSON con todos los parámetros ajustados.
   */
  private validateGetScore(dataGetScore: any): ResponseBiocatchGeo {
    const newData = new ResponseBiocatchGeo();
    Object.keys(dataGetScore).map(key => newData[key] = dataGetScore[key]);
    return newData;
  }

  /**
   * Función para revisar la puntuación
   *
   * *Check score function*
   *
   * @param {boolean} showLoading
   * @returns {Promise}
   */
  checkScore(showLoading: boolean): Promise<any> {
    return this.getScore(showLoading).then(result => {
      console.log('Result Score: ', result);
      this.cryptoVaultService.refreshKeyCrypto();
      this.currentScoreInfo = result;
      result.rule = 'score';
      result.ip_geo_country = result.ipGeoCountry;
      const bitacoraEvent = `geo_bioscore_${this.currentScoreInfo.score}`;
      this.saveDataInBitacora(bitacoraEvent, showLoading);
      return this.checkParameterResults({ data: result }, showLoading);
    });
  }

  /**
   * Función para obtener la puntuación
   *
   * *Gets score function*
   *
   * @param {string} activityType
   * @param {boolean} _showLoading
   *
   * @returns {Promise}
   */
  private getScore(_showLoading?: boolean): Promise<any> {
    console.warn('Ya ingresó a GetScore');
    if (this.currentCSID === null || this.currentCSID === '') {
      throw new Error('currentCSID must be initialized');
    }

    if (!!this.cdApi) {
      console.warn('Ingresó a validaciones GetScore...');
      const activityType = this.isLogin ? this.bodyFields.activity_type_post : this.bodyFields.activity_type_pre;
      const config = this.abreCuentasService.getBioCatchConfig();
      const channel = this.channelCommunicatorService.getChannelId();
      const product = this.abreCuentasService.getproductSelected().product_type;
      const brand = this.bodyFields.brand;
      this.bodyFields.payeeAccountType = this.nitAlly;
      const ppid = this.isLogin ? this.userPPID : config.uuid;
      const csid = (!this.isLogin && this.isAppUnica) ? this.currentCSID : this.abreCuentasService.getCSID();
      const solution = this.isLogin ? this.bodyFields.method_description : config.solution;
      const applicationId = this.isLogin ? undefined : `${channel}-${product}`;
      const ip = this.abreCuentasService.getIp();
      const deviceModel = this.abreCuentasService.getDeviceInfo().model;
      let birthDate;
      if ((this.abreCuentasService.getCustomerData() && Object.keys(this.abreCuentasService.getCustomerData()).length !== 0)) {
        birthDate = this.abreCuentasService.getCustomerData().basic.birthdate as string;
      } else {
        birthDate = this.abreCuentasService.getDocumentInfo().birthdate as string;
      }
      try {
        birthDate = new Date(birthDate.split('/').reverse().join('-')).toString();
      } catch {
        birthDate = undefined;
      }
      const birthYear = birthDate ? new Date(birthDate).getFullYear().toString() : undefined;
      return this.requestService.getScore(config, this.bodyFields, csid, activityType, brand, ppid, solution, deviceModel,
        ip, applicationId, birthYear);
    }
    return Promise.reject({ message: 'cdApi not configured' });
  }

  /**
   * Función que guarda la información en la bitácora
   *
   * Save info in Bitacora
   *
   * @param {any} event
   * @param {boolean} _showLoading
   */
  private async saveDataInBitacora(event: any, _showLoading?: boolean) {
    const ipBiocatch = this.getCurrentScoreIp();
    const bitacoraEvent = event;
    this.devicePrint = new LogDeviceModel();
    this.devicePrint.device = this.abreCuentasService.getDeviceInfo();
    const frontmicro = 'onboarding-angular-fe';
    this.devicePrint.transaction = this.abreCuentasService.getTransactionInfo();
    this.devicePrint.transaction.transaction_type = frontmicro;
    this.devicePrint.device.event_name = bitacoraEvent;
    this.devicePrint.device.ip = ipBiocatch;
    this.devicePrint.terms_type = bitacoraEvent;
    console.log('---------saveDataInBitacora--------');
    console.log(this.devicePrint);

    await this.requestService.saveDataInBitacora(this.devicePrint);
  }

  /**
   * Función de revisión de los resultados de los parámetros
   *
   * Check parameter results function
   *
   * @param {any} body
   * @param {boolean} _showLoading
   * @returns {Promise}
   */
  private checkParameterResults(body: any, _showLoading?: boolean): Promise<any> {
    return this.requestService
      .checkScore(body)
      .then((res: any) => {
        return new Promise((resolve, reject) => {
          if (res) {
            if (res.status !== 200) {
              reject({ message: 'error', code: res.status });
            } else {
              resolve(res.body);
            }
          }
          reject({ message: 'no response' });
        });
      });
  }

  /**
   * Función que llama el servicio de update
   *
   * Call update service
   *
   */
  async update() {
    if (!this.isLogin) { return; }
    const productSelected = this.abreCuentasService.getproductSelected();
    const product = productSelected.product_code;
    const birthDate = productSelected.product_type === BUSINESS_CONST.PRODUCT_TYPE_LIVING ?
      this.abreCuentasService.getEmployedContractModel().contractDate as string :
      this.abreCuentasService.getCustomerData().basic.birthdate as string;
    const birthYear = new Date(birthDate).getFullYear();
    const ageRange = this.getAgeRange((new Date().getFullYear() - birthYear));
    const solicitationType = this.parametersService.getBiocatchContextParams().types[product];
    const maritalStatus = this.abreCuentasService.getMaritalStatusInfo();
    const processedMaritalStatus = maritalStatus.marital_status_description ? maritalStatus.marital_status_description : undefined;
    const occupation = this.abreCuentasService.getOccupationSelected() ? this.abreCuentasService.getOccupationSelected().name
      : 'default occupation';
    const deviceModel = this.abreCuentasService.getDeviceInfo() ? this.abreCuentasService.getDeviceInfo().model : 'default model';
    await this.requestService.biocatchUpdate(
      this.bodyFields,
      this.abreCuentasService.getCSID(),
      this.userPPID,
      this.abreCuentasService.getIntentoOtp().toString(),
      occupation,
      birthYear.toString(),
      ageRange,
      solicitationType,
      deviceModel,
      this.abreCuentasService.getIp(),
      processedMaritalStatus,
    ).then(() => {
      return;
    }, () => {
      return;
    });
  }

  /**
   * Función que llama el servicio de ppid y persiste la respuesta
   *
   * *Function that calls ppid service and persists the response*
   * @method get
   */
  callPpid() {
    // this.userPPID = "d4b88ed7dfb511a011b7acde74146915be0d8dfba37a6fc22fd6a6769ef71d76";
    // this.abreCuentasService.setPPID(this.userPPID);
    this.requestService.getPpid(this.bodyFields).then(res => {
      this.userPPID = res.body.data.ppid;
      this.abreCuentasService.setPPID(this.userPPID);
      this.setPpidIsReady(true);
      return;
    }, () => {
      this.setPpidIsReady(false);
      return;
    });
  }

  isEvidenteCase() {
    return (!this.isAppUnica && !this.isLogin);
  }

  /**
   * Funicón que permite validar que el usuario cumpla:
   * 1. Entre por App
   * 2. Debe ser Cliente Nuevo
   * @returns {boolean}
   */
  isAppCN() {
    return (this.isAppUnica && !this.isLogin);
  }

  /**
   * Funciónque que calcula el rango de edad del cliente
   *
   * *Function that calculates the clients age range
   *
   * @param age
   * @returns {string}
   */
  private getAgeRange(age: number): string {
    let lowerNumber = Math.floor(age / 10) * 10;
    let upperNumber = Math.ceil(age / 10) * 10;
    if (lowerNumber === upperNumber) { upperNumber += 10; }
    return `${lowerNumber.toString()}-${upperNumber.toString()}`;
  }

  isVivCn(product: ProductsBiocatchEnum) {
    return product === ProductsBiocatchEnum.VIV && !this.abreCuentasService.getPostlogin() &&
      !this.abreCuentasService.getIsPortal() ? true : false;
  }

  setPpidIsReady(isReady: boolean): void {
    this.ppidIsReady$.next(isReady);
  }
  getPpidIsReady(): Observable<boolean> {
    return this.ppidIsReady$.asObservable();
  }

}
